<template>
	<ViewFeedItem class="view-feed-userfavoritevenues" v-if="venueCollection.models.length">
		<template v-slot:header>
			<ViewLayoutModuleHeader title="My favorite places">
				<RouterLink
					v-bind:to="{
						name: 'PagePlaces',
						query: {
							tab: 'favorites',
						},
					}"
				>
					<strong>View All</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<ChalkyVenueList
				class="horizontal"
				v-bind:useLocation="true"
				v-bind:venueCollection="venueCollection"
				v-on:venue:select="Handle_OnSelectVenue"
			/>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import * as Factory from '@/App/Factory';
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, bind, event, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedUserFavoriteVenues extends ViewBase {
		/**
		 * Maximum number of venues to display in this feed view
		 *
		 * @type number
		 */
		public maxDisplay: number = 6;

		/**
		 * @type ChalkySticks/Collection/Venue
		 */
		@Prop({
			default: () =>
				ChalkySticks.Factory.Venue.collection({
					limit: 32,
					qp: {
						reaction_from: 'me',
						reaction_type: ChalkySticks.Enum.ReactionType.Love,
					},
				}),
		})
		public venueCollection!: ChalkySticks.Collection.Venue;

		/**
		 * @return void
		 */
		@mounted
		public setup(): void {
			const userModel = this.$store.getters['authentication/user'];

			this.venueCollection.setQueryParam('reaction_from', userModel?.id);
			this.venueCollection.fetch();
		}

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			this.venueCollection.on('complete', this.Handle_OnVenueCollectionComplete);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			this.venueCollection.off('complete', this.Handle_OnVenueCollectionComplete);
		}

		/**
		 * @return void
		 */
		protected sortModelsByDistance(): void {
			this.venueCollection.models = this.venueCollection.models
				.sort((venueA, venueB): number => {
					const { latitude, longitude } = this.$store.getters['location/coordinates'];
					const distanceToA = venueA.getDistance(latitude, longitude);
					const distanceToB = venueB.getDistance(latitude, longitude);

					return distanceToA - distanceToB;
				})
				.slice(0, this.maxDisplay);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnVenueCollectionComplete(): Promise<void> {
			this.sortModelsByDistance();
		}

		/**
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.$store.dispatch('gotoVenue', venueModel);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-feed-userfavoritevenues {
		--chalky-utility-gallery-aspect-ratio: 4 / 3;

		.chalky.venue-list.horizontal .venue-card {
			--chalky-venue-list-thumbnail-width: 85%;

			.utility-gallery {
				height: 45svh;
			}
		}

		.chalky.venue-list.horizontal .venue-card .checkins {
			bottom: 50px;
		}
	}
</style>
